<template>
    <v-app id="app">
        <transition name="fade" mode="out-in">
            <router-view></router-view>
        </transition>
        <v-dialog :value="appLockSetupRequired" persistent max-width="420px" :fullscreen="$vuetify.breakpoint.xsOnly">
            <v-card class="app-lock-card">
                <v-card-title class="primary secondary--text app-lock-title justify-center text-center">
                    <v-icon color="secondary" class="mr-2">mdi-numeric-4-box</v-icon>
                    Configurar PIN
                </v-card-title>
                <v-card-text class="app-lock-body pt-4">
                    <v-sheet
                        v-if="showSessionDynamicKey"
                        role="button"
                        title="Copiar clave dinámica"
                        class="app-lock-dynamic-key app-lock-dynamic-key--inline mx-auto mb-3 px-3 py-2"
                        @click="copySessionDynamicKey">
                        <div class="d-flex align-center justify-center">
                            <v-icon small color="primary" class="mr-2">mdi-cellphone-lock</v-icon>
                            <span class="caption primary--text font-weight-bold mr-2">Clave dinámica</span>
                            <span class="subtitle-2 font-weight-bold primary--text">{{ sessionDynamicKey.code }}</span>
                            <v-chip x-small label color="secondary" class="primary--text font-weight-bold ml-2">
                                {{ sessionDynamicKey.remaining }}s
                            </v-chip>
                        </div>
                        <v-progress-linear
                            :value="sessionDynamicKeyProgress"
                            color="primary"
                            background-color="rgba(13, 147, 165, .16)"
                            height="4"
                            rounded
                            class="mt-2"/>
                    </v-sheet>
                    <div class="text-center mb-4">
                        <v-avatar color="primary" size="64" class="secondary--text font-weight-bold mb-3">
                            <img v-if="accountPhoto" :src="accountPhoto" :alt="accountName">
                            <span v-else>{{ accountInitials }}</span>
                        </v-avatar>
                        <div class="subtitle-1 font-weight-bold primary--text">{{ accountName }}</div>
                        <div class="body-2 text--secondary mt-1">
                            {{ setupPinHelpText }}
                        </div>
                    </div>
                    <security-code-pad
                        v-model="setupPinEntry"
                        :length="4"
                        :error-messages="setupPinErrorMessages"
                        :disabled="setupSaving"
                        autofocus
                        @complete="handleSetupPinComplete"
                        @submit="saveInitialAppPin"/>
                </v-card-text>
                <v-divider/>
                <v-card-actions class="app-lock-actions">
                    <v-spacer/>
                    <v-btn
                        color="primary"
                        class="secondary--text"
                        :loading="setupSaving"
                        :disabled="setupActionDisabled"
                        @click="saveInitialAppPin">
                        {{ setupActionText }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="locked" persistent max-width="420px" :fullscreen="$vuetify.breakpoint.xsOnly">
            <v-card class="app-lock-card">
                <v-card-title class="primary secondary--text app-lock-title justify-center text-center">
                    <v-icon color="secondary" class="mr-2">mdi-cellphone-lock</v-icon>
                    Desbloquear Valzum
                </v-card-title>
                <v-card-text class="app-lock-body pt-4">
                    <v-sheet
                        v-if="showSessionDynamicKey"
                        role="button"
                        title="Copiar clave dinámica"
                        class="app-lock-dynamic-key app-lock-dynamic-key--inline mx-auto mb-3 px-3 py-2"
                        @click="copySessionDynamicKey">
                        <div class="d-flex align-center justify-center">
                            <v-icon small color="primary" class="mr-2">mdi-cellphone-lock</v-icon>
                            <span class="caption primary--text font-weight-bold mr-2">Clave dinámica</span>
                            <span class="subtitle-2 font-weight-bold primary--text">{{ sessionDynamicKey.code }}</span>
                            <v-chip x-small label color="secondary" class="primary--text font-weight-bold ml-2">
                                {{ sessionDynamicKey.remaining }}s
                            </v-chip>
                        </div>
                        <v-progress-linear
                            :value="sessionDynamicKeyProgress"
                            color="primary"
                            background-color="rgba(13, 147, 165, .16)"
                            height="4"
                            rounded
                            class="mt-2"/>
                    </v-sheet>
                    <div class="text-center mb-4">
                        <v-avatar color="secondary" size="64" class="mb-3">
                            <v-icon color="primary" large>{{ appLockIcon }}</v-icon>
                        </v-avatar>
                        <div class="subtitle-1 font-weight-bold primary--text">{{ appLockTitle }}</div>
                    </div>
                    <v-sheet class="app-lock-account mb-4" outlined>
                        <div class="d-flex align-center">
                            <v-avatar color="primary" size="44" class="secondary--text font-weight-bold mr-3">
                                <img v-if="accountPhoto" :src="accountPhoto" :alt="accountName">
                                <span v-else>{{ accountInitials }}</span>
                            </v-avatar>
                            <div class="min-width-0">
                                <div class="subtitle-2 font-weight-bold text-truncate">{{ accountName }}</div>
                                <div v-if="showAccountRole" class="caption primary--text font-weight-medium">{{ accountRole }}</div>
                            </div>
                        </div>
                    </v-sheet>
                    <security-code-pad
                        v-if="appLockMethod === 'pin'"
                        v-model="pin"
                        :length="4"
                        :error-messages="error"
                        :disabled="unlocking"
                        autofocus
                        @input="handlePinInput"
                        @complete="verifyPin"
                        @submit="verifyPin"/>
                    <v-btn
                        v-if="appLockMethod === 'device'"
                        block
                        large
                        color="primary"
                        class="secondary--text"
                        :loading="unlocking"
                        @click="unlockWithDeviceSecurity">
                        <v-icon left>mdi-fingerprint</v-icon>Usar seguridad del dispositivo
                    </v-btn>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="idleLocked" persistent :max-width="idleLockDialogMaxWidth" :fullscreen="idleLockFullscreen">
            <v-card :class="['app-lock-card', {'app-lock-card--fullscreen': idleLockFullscreen, 'app-lock-card--staff': staffLockScreen}]">
                <template v-if="staffLockScreen">
                    <div class="staff-lock-screen">
                        <div class="staff-lock-top">
                            <v-img src="/img/logo-valzum-white.png" class="staff-lock-logo" contain/>
                            <v-chip label small class="staff-lock-chip">
                                <v-icon small left>mdi-shield-check</v-icon>
                                Sesion protegida
                            </v-chip>
                        </div>
                        <div class="staff-lock-content">
                            <v-sheet class="staff-lock-panel" elevation="14">
                                <div class="staff-lock-account">
                                    <v-avatar color="primary" size="72" class="secondary--text font-weight-bold staff-lock-avatar">
                                        <img v-if="accountPhoto" :src="accountPhoto" :alt="accountName">
                                        <span v-else>{{ accountInitials }}</span>
                                    </v-avatar>
                                    <div class="min-width-0">
                                        <div class="staff-lock-name text-truncate">{{ accountName }}</div>
                                        <v-chip v-if="showAccountRole" x-small label color="secondary" class="primary--text font-weight-bold mt-1">
                                            {{ accountRole }}
                                        </v-chip>
                                    </div>
                                </div>
                                <div class="staff-lock-copy">
                                    <div class="staff-lock-heading">{{ passwordConfirmTitle }}</div>
                                    <div class="staff-lock-message">{{ passwordConfirmMessage }}</div>
                                </div>
                                <v-sheet
                                    v-if="showSessionDynamicKey"
                                    role="button"
                                    title="Copiar clave dinámica"
                                    class="app-lock-dynamic-key app-lock-dynamic-key--inline mx-auto mb-4 px-3 py-2"
                                    @click="copySessionDynamicKey">
                                    <div class="d-flex align-center justify-center">
                                        <v-icon small color="primary" class="mr-2">mdi-cellphone-lock</v-icon>
                                        <span class="caption primary--text font-weight-bold mr-2">Clave dinámica</span>
                                        <span class="subtitle-2 font-weight-bold primary--text">{{ sessionDynamicKey.code }}</span>
                                        <v-chip x-small label color="secondary" class="primary--text font-weight-bold ml-2">
                                            {{ sessionDynamicKey.remaining }}s
                                        </v-chip>
                                    </div>
                                    <v-progress-linear
                                        :value="sessionDynamicKeyProgress"
                                        color="primary"
                                        background-color="rgba(13, 147, 165, .16)"
                                        height="4"
                                        rounded
                                        class="mt-2"/>
                                </v-sheet>
                                <v-text-field
                                    v-model="idlePassword"
                                    label="Clave de acceso"
                                    :type="idlePasswordHidden ? 'password' : 'text'"
                                    outlined
                                    autofocus
                                    prepend-inner-icon="mdi-lock"
                                    :append-icon="idlePasswordHidden ? 'mdi-eye' : 'mdi-eye-off'"
                                    :error-messages="idleError"
                                    :disabled="idleConfirming"
                                    @click:append="idlePasswordHidden = !idlePasswordHidden"
                                    @keyup.enter="confirmIdlePassword"/>
                                <v-btn
                                    block
                                    large
                                    color="primary"
                                    class="secondary--text mt-2"
                                    :loading="idleConfirming"
                                    :disabled="!idlePassword"
                                    @click="confirmIdlePassword">
                                    Confirmar acceso
                                </v-btn>
                            </v-sheet>
                        </div>
                        <div class="staff-lock-footer">
                            Tu sesion sigue abierta. Confirma tu clave para continuar.
                        </div>
                    </div>
                </template>
                <template v-else>
                    <v-card-title class="primary secondary--text app-lock-title justify-center text-center">
                        <v-icon color="secondary" class="mr-2">mdi-shield-lock</v-icon>
                        Confirmar acceso
                    </v-card-title>
                    <v-card-text class="app-lock-body pt-3">
                        <v-sheet
                            v-if="showSessionDynamicKey"
                            role="button"
                            title="Copiar clave dinámica"
                            class="app-lock-dynamic-key app-lock-dynamic-key--inline mx-auto mb-3 px-3 py-2"
                            @click="copySessionDynamicKey">
                            <div class="d-flex align-center justify-center">
                                <v-icon small color="primary" class="mr-2">mdi-cellphone-lock</v-icon>
                                <span class="caption primary--text font-weight-bold mr-2">Clave dinámica</span>
                                <span class="subtitle-2 font-weight-bold primary--text">{{ sessionDynamicKey.code }}</span>
                                <v-chip x-small label color="secondary" class="primary--text font-weight-bold ml-2">
                                    {{ sessionDynamicKey.remaining }}s
                                </v-chip>
                            </div>
                            <v-progress-linear
                                :value="sessionDynamicKeyProgress"
                                color="primary"
                                background-color="rgba(13, 147, 165, .16)"
                                height="4"
                                rounded
                                class="mt-2"/>
                        </v-sheet>
                        <div class="text-center mb-3">
                            <v-avatar color="secondary" size="52" class="mb-2">
                                <v-icon color="primary" large>mdi-account-lock</v-icon>
                            </v-avatar>
                            <div class="subtitle-1 font-weight-bold primary--text">
                                {{ passwordConfirmTitle }}
                            </div>
                            <div class="body-2 text--secondary mt-1">
                                {{ passwordConfirmMessage }}
                            </div>
                        </div>
                        <v-sheet class="app-lock-account mb-4" outlined>
                            <div class="d-flex align-center">
                                <v-avatar color="primary" size="44" class="secondary--text font-weight-bold mr-3">
                                    <img v-if="accountPhoto" :src="accountPhoto" :alt="accountName">
                                    <span v-else>{{ accountInitials }}</span>
                                </v-avatar>
                                <div class="min-width-0">
                                    <div class="subtitle-2 font-weight-bold text-truncate">{{ accountName }}</div>
                                    <div v-if="showAccountRole" class="caption primary--text font-weight-medium">{{ accountRole }}</div>
                                </div>
                            </div>
                        </v-sheet>
                        <v-text-field
                            v-model="idlePassword"
                            label="Clave de acceso"
                            :type="idlePasswordHidden ? 'password' : 'text'"
                            filled
                            autofocus
                            prepend-icon="mdi-lock"
                            :append-icon="idlePasswordHidden ? 'mdi-eye' : 'mdi-eye-off'"
                            :error-messages="idleError"
                            :disabled="idleConfirming"
                            @click:append="idlePasswordHidden = !idlePasswordHidden"
                            @keyup.enter="confirmIdlePassword"/>
                    </v-card-text>
                    <v-divider/>
                    <v-card-actions class="app-lock-actions">
                        <v-spacer/>
                        <v-btn
                            color="primary"
                            class="secondary--text"
                            :loading="idleConfirming"
                            :disabled="!idlePassword"
                            @click="confirmIdlePassword">
                            Confirmar
                        </v-btn>
                    </v-card-actions>
                </template>
            </v-card>
        </v-dialog>
    </v-app>
</template>

<script>
import axios from 'axios';
import { settings } from '../config';
import {devicePayload} from '../helpers/device';
import SecurityCodePad from './SecurityCodePad';

export default {
    components: {SecurityCodePad},
    data: () => ({
        locked: false,
        pin: '',
        lastPinSubmitted: '',
        error: '',
        unlocking: false,
        setupPin: '',
        setupPinConfirmation: '',
        setupPinEntry: '',
        setupPinStep: 'pin',
        setupErrors: {},
        setupSaving: false,
        hiddenAt: null,
        idleLocked: false,
        idlePassword: '',
        idlePasswordHidden: true,
        idleError: '',
        idleConfirming: false,
        idleReason: '',
        idleTimer: null,
        sessionDynamicKeyLoading: false,
        sessionDynamicKey: {
            enabled: false,
            code: null,
            period: 30,
            remaining: 0,
            expires_at: null,
            timer: null
        },
        idleEvents: ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click']
    }),
    computed: {
        user() {
            return this.$store.getters.user || {};
        },
        isCustomer() {
            return this.user && this.user.role === 'Customer';
        },
        isNativeApp() {
            return settings.is_native_app === true;
        },
        nativeCustomer() {
            return this.isNativeApp && this.isCustomer;
        },
        nativeDynamicKeyDevice() {
            const device = this.devicePayload();
            const deviceName = String(device.device || '').toLowerCase();

            return device.name === 'Valzum'
                && ['android', 'ios', 'iphone', 'ipad'].indexOf(deviceName) !== -1
                && !!(device.device_uid || device.ios_id || device.fcm_token);
        },
        dynamicKeyScreenVisible() {
            return this.appLockSetupRequired || this.locked || this.idleLocked;
        },
        showSessionDynamicKey() {
            return this.nativeDynamicKeyDevice
                && this.sessionDynamicKey.enabled
                && this.sessionDynamicKey.code;
        },
        sessionDynamicKeyProgress() {
            const period = Number(this.sessionDynamicKey.period || 30);
            const remaining = Number(this.sessionDynamicKey.remaining || 0);

            return period > 0 ? Math.max(0, Math.min(100, (remaining / period) * 100)) : 0;
        },
        appLockSetupRequired() {
            return this.$store.getters.check
                && this.nativeCustomer
                && !(this.user.app_lock_pin_set === true || this.user.app_lock_pin_set === 1);
        },
        appLockEnabled() {
            return this.nativeCustomer && (this.user.app_lock_pin_set === true || this.user.app_lock_pin_set === 1);
        },
        appLockMethod() {
            return 'pin';
        },
        appLockApplies() {
            if (this.appLockSetupRequired) {
                return false;
            }

            return this.nativeCustomer && this.appLockEnabled;
        },
        appLockTimeoutMs() {
            return Math.max(0, Number(this.user.app_lock_timeout_minutes || 0)) * 60000;
        },
        appLockIcon() {
            return this.appLockMethod === 'device' ? 'mdi-fingerprint' : 'mdi-numeric-4-box';
        },
        appLockTitle() {
            return this.appLockMethod === 'device'
                ? 'Confirma con la seguridad de tu dispositivo'
                : 'Ingresa tu PIN para continuar';
        },
        setupPinHelpText() {
            return this.setupPinStep === 'confirm'
                ? 'Vuelve a ingresar el PIN para confirmarlo.'
                : 'Crea un PIN de 4 numeros para proteger la app.';
        },
        setupPinErrorMessages() {
            return this.setupPinStep === 'confirm'
                ? (this.setupErrors.app_lock_pin_confirmation || [])
                : (this.setupErrors.app_lock_pin || []);
        },
        setupActionText() {
            return this.setupPinStep === 'confirm' ? 'Guardar PIN' : 'Continuar';
        },
        setupActionDisabled() {
            return this.setupSaving || this.setupPinEntry.length !== 4;
        },
        browserIdleApplies() {
            return this.$store.getters.check && !this.isNativeApp && this.user && !!this.user.role;
        },
        staffIdleApplies() {
            return this.browserIdleApplies && !this.isCustomer;
        },
        customerIdleApplies() {
            return this.browserIdleApplies && this.isCustomer;
        },
        idleTimeoutMs() {
            return 15 * 60 * 1000;
        },
        nativePasswordConfirmApplies() {
            return this.$store.getters.check && this.isNativeApp && !this.appLockSetupRequired && this.user && !!this.user.role;
        },
        nativePasswordConfirmMs() {
            return 30 * 24 * 60 * 60 * 1000;
        },
        nativePasswordStorageKey() {
            const id = this.user && this.user.id ? this.user.id : (this.user.email || 'current');

            return 'valzum_native_password_confirmed_at_' + id;
        },
        staffLockScreen() {
            return this.idleLocked && !this.isCustomer;
        },
        idleLockFullscreen() {
            return this.staffLockScreen || this.$vuetify.breakpoint.xsOnly;
        },
        idleLockDialogMaxWidth() {
            return this.idleLockFullscreen ? undefined : '420px';
        },
        passwordConfirmTitle() {
            if (this.idleReason === 'native_periodic') {
                return 'Confirma tu clave para continuar';
            }

            return 'La sesion quedo protegida por inactividad';
        },
        passwordConfirmMessage() {
            if (this.idleReason === 'native_periodic') {
                return 'Por seguridad, confirma la clave de esta cuenta cada 30 dias.';
            }

            return 'Ingresa tu clave para continuar.';
        },
        accountName() {
            const name = [this.user.name, this.user.lname].filter(Boolean).join(' ').trim();

            return name || this.user.email || 'Cuenta Valzum';
        },
        accountInitials() {
            const words = this.accountName.split(' ').filter(Boolean);
            const first = words[0] ? words[0].charAt(0) : 'V';
            const second = words.length > 1 ? words[1].charAt(0) : '';

            return (first + second).toUpperCase();
        },
        accountPhoto() {
            return this.user.photo || this.user.avatar || this.user.image || '';
        },
        showAccountRole() {
            return !this.isCustomer && !!this.user.role;
        },
        accountRole() {
            const roles = {
                Customer: 'Cliente',
                Administrator: 'Administrador',
                Admin: 'Administrador',
                Support: 'Soporte',
                Cashier: 'Cajero',
                Payer: 'Pagador',
                Checker: 'Verificador',
                Auditor: 'Auditor',
                Monitor: 'Monitor',
                Agent: 'Agente'
            };

            return roles[this.user.role] || this.user.role || 'Usuario';
        }
    },
    watch: {
        user: {
            immediate: true,
            handler() {
                this.evaluateLock(false);
                this.evaluateIdleState();
                this.evaluateNativePasswordConfirmation();
            }
        },
        idleLocked(value) {
            if (value) {
                this.loadSessionDynamicKey();
            } else {
                this.stopSessionDynamicKeyIfHidden();
            }
        },
        locked(value) {
            if (value) {
                this.loadSessionDynamicKey();
            } else {
                this.stopSessionDynamicKeyIfHidden();
            }
        },
        appLockSetupRequired(value) {
            if (value) {
                this.loadSessionDynamicKey();
            } else {
                this.stopSessionDynamicKeyIfHidden();
            }
        },
        '$route.name'() {
            this.evaluateLock(false);
            this.recordIdleActivity();
            this.evaluateNativePasswordConfirmation();
        }
    },
    mounted() {
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('focus', this.handleFocus);
        window.addEventListener('valzum-native-app-paused', this.handleNativeAppPaused);
        window.addEventListener('valzum-native-app-resumed', this.handleNativeAppResumed);
        window.addEventListener('valzum-app-lock-unlocked', this.markUnlocked);
        window.valzumNativeAppPaused = this.handleNativeAppPaused;
        window.valzumNativeAppResumed = this.handleNativeAppResumed;
        window.valzumRequireSessionConfirmation = this.requireSessionConfirmation;
        window.valzumAppLockUnlocked = this.markUnlocked;
        this.idleEvents.forEach(event => {
            window.addEventListener(event, this.handleIdleActivity, {passive: true});
        });
        this.idleTimer = setInterval(this.checkSecurityTimers, 30000);
        this.evaluateIdleState();
        this.evaluateNativePasswordConfirmation();
        if (this.dynamicKeyScreenVisible) {
            this.loadSessionDynamicKey();
        }
    },
    beforeDestroy() {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('focus', this.handleFocus);
        window.removeEventListener('valzum-native-app-paused', this.handleNativeAppPaused);
        window.removeEventListener('valzum-native-app-resumed', this.handleNativeAppResumed);
        window.removeEventListener('valzum-app-lock-unlocked', this.markUnlocked);
        this.idleEvents.forEach(event => {
            window.removeEventListener(event, this.handleIdleActivity);
        });
        if (this.idleTimer) {
            clearInterval(this.idleTimer);
            this.idleTimer = null;
        }
        this.stopSessionDynamicKeyTimer();
        if (window.valzumAppLockUnlocked === this.markUnlocked) {
            delete window.valzumAppLockUnlocked;
        }
        if (window.valzumNativeAppPaused === this.handleNativeAppPaused) {
            delete window.valzumNativeAppPaused;
        }
        if (window.valzumNativeAppResumed === this.handleNativeAppResumed) {
            delete window.valzumNativeAppResumed;
        }
        if (window.valzumRequireSessionConfirmation === this.requireSessionConfirmation) {
            delete window.valzumRequireSessionConfirmation;
        }
    },
    methods: {
        devicePayload() {
            return devicePayload();
        },
        copySessionDynamicKey() {
            if (!this.sessionDynamicKey.code) return;

            this.copyToClipboard(String(this.sessionDynamicKey.code));
        },
        copyToClipboard(value) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(value).then(() => {
                    this.$toast.success('Clave copiada.', {icon: 'mdi-content-copy'});
                }).catch(() => {
                    this.copyToClipboardFallback(value);
                });
                return;
            }

            this.copyToClipboardFallback(value);
        },
        copyToClipboardFallback(value) {
            const input = document.createElement('textarea');
            input.value = value;
            input.setAttribute('readonly', 'readonly');
            input.style.position = 'fixed';
            input.style.opacity = '0';
            document.body.appendChild(input);
            input.select();

            try {
                document.execCommand('copy');
                this.$toast.success('Clave copiada.', {icon: 'mdi-content-copy'});
            } catch (error) {
                this.$toast.error('No se pudo copiar.', {icon: 'mdi-alert-circle'});
            } finally {
                document.body.removeChild(input);
            }
        },
        evaluateIdleState() {
            if (!this.browserIdleApplies) {
                if (this.idleReason !== 'native_periodic') {
                    this.idleLocked = false;
                    this.idlePassword = '';
                    this.idlePasswordHidden = true;
                    this.idleError = '';
                    this.idleReason = '';
                }
                sessionStorage.removeItem('valzum_staff_idle_locked');
                return;
            }

            if (!sessionStorage.getItem('valzum_idle_last_activity_at')) {
                this.recordIdleActivity();
                return;
            }

            if (this.staffIdleApplies && sessionStorage.getItem('valzum_staff_idle_locked') === '1') {
                this.lockStaffSession();
                return;
            }

            this.checkIdleTimeout();
        },
        handleIdleActivity() {
            if (!this.browserIdleApplies || this.idleLocked) {
                return;
            }

            const lastActivity = Number(sessionStorage.getItem('valzum_idle_last_activity_at') || 0);
            if (lastActivity && Date.now() - lastActivity >= this.idleTimeoutMs) {
                this.checkIdleTimeout();
                return;
            }

            this.recordIdleActivity();
        },
        recordIdleActivity() {
            if (!this.browserIdleApplies || this.idleLocked) {
                return;
            }

            sessionStorage.setItem('valzum_idle_last_activity_at', String(Date.now()));
        },
        checkIdleTimeout() {
            if (!this.browserIdleApplies || this.idleLocked) {
                return;
            }

            const lastActivity = Number(sessionStorage.getItem('valzum_idle_last_activity_at') || 0);
            if (!lastActivity) {
                this.recordIdleActivity();
                return;
            }

            if (Date.now() - lastActivity < this.idleTimeoutMs) {
                return;
            }

            if (this.customerIdleApplies) {
                this.expireCustomerSession();
                return;
            }

            if (this.staffIdleApplies) {
                this.lockStaffSession();
            }
        },
        checkSecurityTimers() {
            this.checkIdleTimeout();
            this.evaluateNativePasswordConfirmation();
        },
        evaluateNativePasswordConfirmation() {
            if (!this.nativePasswordConfirmApplies) {
                if (this.idleReason === 'native_periodic') {
                    this.idleLocked = false;
                    this.idlePassword = '';
                    this.idlePasswordHidden = true;
                    this.idleError = '';
                    this.idleReason = '';
                }
                sessionStorage.removeItem('valzum_native_password_due');
                return;
            }

            if (this.recentLogin()) {
                this.markNativePasswordConfirmed();
                return;
            }

            if (this.idleLocked && this.idleReason !== 'native_periodic') {
                return;
            }

            if (sessionStorage.getItem('valzum_native_password_due') === '1') {
                this.lockNativePasswordConfirmation();
                return;
            }

            const confirmedAt = Number(localStorage.getItem(this.nativePasswordStorageKey) || 0);
            if (!confirmedAt) {
                this.markNativePasswordConfirmed();
                return;
            }

            if (Date.now() - confirmedAt >= this.nativePasswordConfirmMs) {
                this.lockNativePasswordConfirmation();
            }
        },
        lockNativePasswordConfirmation() {
            this.idleReason = 'native_periodic';
            this.idleLocked = true;
            this.idlePassword = '';
            this.idlePasswordHidden = true;
            this.idleError = '';
            sessionStorage.setItem('valzum_native_password_due', '1');
        },
        markNativePasswordConfirmed() {
            if (!this.nativePasswordConfirmApplies) {
                return;
            }

            localStorage.setItem(this.nativePasswordStorageKey, String(Date.now()));
            sessionStorage.removeItem('valzum_native_password_due');
        },
        expireCustomerSession() {
            sessionStorage.removeItem('valzum_idle_last_activity_at');
            this.$store.dispatch('logout').then(() => {
                this.idleLocked = false;
                this.locked = false;
                if (this.$route.name !== 'Login') {
                    this.$router.push({name: 'Login'}).catch(() => {});
                }
                this.$toast.info('Cerramos tu sesion por inactividad.', {icon: 'mdi-timer-lock'});
            });
        },
        lockStaffSession() {
            this.idleReason = 'staff_idle';
            this.idleLocked = true;
            this.idlePassword = '';
            this.idlePasswordHidden = true;
            this.idleError = '';
            sessionStorage.setItem('valzum_staff_idle_locked', '1');
        },
        requireSessionConfirmation() {
            if (!this.staffIdleApplies) {
                return;
            }

            this.lockStaffSession();
        },
        confirmIdlePassword() {
            if (!this.idlePassword || this.idleConfirming) {
                return;
            }

            this.idleConfirming = true;
            this.idleError = '';
            axios.post('/api/session/confirm-password', {
                password: this.idlePassword
            }).then(res => {
                const reason = this.idleReason;

                this.idleLocked = false;
                this.idlePassword = '';
                this.idlePasswordHidden = true;
                this.idleReason = '';
                if (reason === 'native_periodic') {
                    this.markNativePasswordConfirmed();
                } else {
                    sessionStorage.removeItem('valzum_staff_idle_locked');
                    this.recordIdleActivity();
                }
                window.dispatchEvent(new CustomEvent('valzum-session-confirmed', {
                    detail: {reason}
                }));
                this.$toast.success(res.data.status || 'Sesion confirmada.', {icon: 'mdi-shield-check'});
            }).catch(err => {
                if (err.response && err.response.status === 429) {
                    const retryAfter = Number(err.response.headers && err.response.headers['retry-after'] ? err.response.headers['retry-after'] : 0);
                    this.idleError = retryAfter > 0
                        ? `Espera ${retryAfter} segundos antes de volver a intentar.`
                        : 'Espera un momento antes de volver a intentar.';
                    return;
                }

                this.idleError = err.response && err.response.data && err.response.data.status
                    ? err.response.data.status
                    : 'No fue posible confirmar la clave.';
            }).then(() => {
                this.idleConfirming = false;
            });
        },
        loadSessionDynamicKey() {
            if (!this.nativeDynamicKeyDevice) {
                this.setSessionDynamicKey(null);
                return;
            }

            if (this.sessionDynamicKeyLoading) {
                return;
            }

            this.sessionDynamicKeyLoading = true;
            axios.get('/api/security/dynamic-key', {
                params: {
                    device_info: this.devicePayload()
                }
            }).then(res => {
                this.setSessionDynamicKey(res.data.dynamic_key || null);
            }).catch(() => {
                this.setSessionDynamicKey(null);
            }).then(() => {
                this.sessionDynamicKeyLoading = false;
            });
        },
        setSessionDynamicKey(payload) {
            this.stopSessionDynamicKeyTimer();
            this.sessionDynamicKey = Object.assign({
                enabled: false,
                code: null,
                period: 30,
                remaining: 0,
                expires_at: null,
                timer: null
            }, payload || {});

            if (this.sessionDynamicKey.enabled && this.sessionDynamicKey.expires_at) {
                this.startSessionDynamicKeyTimer();
            }
        },
        startSessionDynamicKeyTimer() {
            this.stopSessionDynamicKeyTimer();

            if (!this.sessionDynamicKey.enabled) return;

            this.tickSessionDynamicKey();
            this.sessionDynamicKey.timer = setInterval(() => {
                this.tickSessionDynamicKey();
            }, 1000);
        },
        stopSessionDynamicKeyTimer() {
            if (this.sessionDynamicKey.timer) {
                clearInterval(this.sessionDynamicKey.timer);
                this.sessionDynamicKey.timer = null;
            }
        },
        stopSessionDynamicKeyIfHidden() {
            if (this.dynamicKeyScreenVisible) {
                return;
            }

            this.stopSessionDynamicKeyTimer();
            this.setSessionDynamicKey(null);
        },
        tickSessionDynamicKey() {
            if (!this.sessionDynamicKey.enabled || !this.sessionDynamicKey.expires_at) return;

            const expiresAt = Date.parse(this.sessionDynamicKey.expires_at);
            if (!expiresAt) return;

            const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
            this.$set(this.sessionDynamicKey, 'remaining', remaining);

            if (remaining <= 1 && this.dynamicKeyScreenVisible) {
                this.loadSessionDynamicKey();
            }
        },
        evaluateLock(force) {
            if (this.appLockSetupRequired) {
                this.locked = false;
                return;
            }

            if (!this.appLockApplies) {
                this.locked = false;
                return;
            }

            if (this.recentLogin()) {
                this.markUnlocked();
                return;
            }

            const unlockedAt = Number(sessionStorage.getItem('valzum_app_lock_unlocked_at') || 0);
            if (!force && unlockedAt > 0) {
                return;
            }

            this.locked = true;
        },
        handleVisibilityChange() {
            if (!this.appLockApplies) return;

            if (document.hidden) {
                this.markAppLockLeftAt(Date.now());
                return;
            }

            this.lockAfterAway();
        },
        handleNativeAppPaused() {
            if (!this.appLockApplies) return;

            this.markAppLockLeftAt(Date.now());
        },
        handleNativeAppResumed() {
            this.lockAfterAway();
            this.evaluateNativePasswordConfirmation();
        },
        markAppLockLeftAt(value) {
            this.hiddenAt = value;
            sessionStorage.setItem('valzum_app_lock_left_at', String(value));
        },
        handleFocus() {
            this.lockAfterAway();
        },
        lockAfterAway() {
            if (!this.appLockApplies || this.locked) return;

            const leftAt = this.hiddenAt || Number(sessionStorage.getItem('valzum_app_lock_left_at') || 0);
            if (!leftAt) return;

            if (Date.now() - leftAt >= this.appLockTimeoutMs) {
                this.pin = '';
                this.error = '';
                sessionStorage.removeItem('valzum_app_lock_unlocked_at');
                this.locked = true;
            }
        },
        verifyPin() {
            if (!this.nativeCustomer) {
                this.markUnlocked();
                return;
            }

            this.pin = this.onlyDigits(this.pin);
            if (this.pin.length !== 4 || this.unlocking) return;

            this.unlocking = true;
            this.error = '';
            this.lastPinSubmitted = this.pin;
            axios.post('/api/preferences/app-lock/verify', {
                pin: this.pin,
                device_info: this.devicePayload()
            }).then(() => {
                this.markUnlocked();
            }).catch(err => {
                this.error = err.response && err.response.data && err.response.data.status
                    ? err.response.data.status
                    : 'No fue posible desbloquear.';
                this.pin = '';
                this.lastPinSubmitted = '';
            }).then(() => {
                this.unlocking = false;
            });
        },
        handlePinInput(value) {
            const normalized = this.onlyDigits(value);
            if (normalized !== this.lastPinSubmitted) {
                this.error = '';
            }
        },
        saveInitialAppPin() {
            if (!this.nativeCustomer) {
                return;
            }

            this.setupPinEntry = this.onlyDigits(this.setupPinEntry);
            this.setupErrors = {};

            if (this.setupPinEntry.length !== 4) {
                this.setupErrors = {
                    app_lock_pin: ['Ingresa un PIN de 4 numeros.']
                };
                return;
            }

            if (this.setupPinStep !== 'confirm') {
                this.setupPin = this.setupPinEntry;
                this.setupPinEntry = '';
                this.setupPinStep = 'confirm';
                return;
            }

            this.setupPinConfirmation = this.setupPinEntry;
            if (this.setupPin !== this.setupPinConfirmation) {
                this.setupErrors = {
                    app_lock_pin_confirmation: ['La confirmacion no coincide.']
                };
                this.setupPinEntry = '';
                return;
            }

            this.setupSaving = true;
            axios.post('/api/preferences', {
                app_lock_enabled: true,
                app_lock_method: 'pin',
                app_lock_timeout_minutes: 0,
                app_lock_pin: this.setupPin,
                app_lock_pin_confirmation: this.setupPinConfirmation,
                device_info: this.devicePayload()
            }).then(res => {
                if (res.data && res.data.user) {
                    this.$store.dispatch('setUser', {user: res.data.user});
                }
                this.setupPin = '';
                this.setupPinConfirmation = '';
                this.setupPinEntry = '';
                this.setupPinStep = 'pin';
                this.markUnlocked();
                this.markNativePasswordConfirmed();
                this.$toast.success(res.data.status || 'PIN configurado.', {icon: 'mdi-shield-check'});
            }).catch(err => {
                if (err.response && err.response.data && err.response.data.errors) {
                    this.setupErrors = err.response.data.errors;
                    return;
                }

                this.setupErrors = {
                    app_lock_pin: ['No fue posible configurar el PIN.']
                };
            }).then(() => {
                this.setupSaving = false;
            });
        },
        handleSetupPinComplete() {
            setTimeout(() => {
                if (!this.setupSaving && this.setupPinEntry.length === 4) {
                    this.saveInitialAppPin();
                }
            }, 120);
        },
        unlockWithDeviceSecurity() {
            this.unlocking = true;
            this.error = '';

            try {
                if (window.ValzumApp && typeof window.ValzumApp.unlockApp === 'function') {
                    const result = window.ValzumApp.unlockApp();
                    if (result === true || result === 'success') {
                        this.markUnlocked();
                    }
                } else if (
                    window.webkit
                    && window.webkit.messageHandlers
                    && window.webkit.messageHandlers.valzumAppLock
                ) {
                    window.webkit.messageHandlers.valzumAppLock.postMessage({action: 'unlock'});
                }
            } catch (error) {
                this.error = 'No fue posible usar la seguridad del dispositivo.';
            }

            setTimeout(() => {
                this.unlocking = false;
            }, 1200);
        },
        markUnlocked() {
            this.pin = '';
            this.lastPinSubmitted = '';
            this.error = '';
            this.locked = false;
            this.hiddenAt = null;
            sessionStorage.setItem('valzum_app_lock_unlocked_at', String(Date.now()));
            sessionStorage.removeItem('valzum_app_lock_left_at');
        },
        recentLogin() {
            const lastAuthAt = Number(sessionStorage.getItem('valzum_last_auth_at') || 0);

            return lastAuthAt > 0 && Date.now() - lastAuthAt < 12000;
        },
        onlyDigits(value) {
            return String(value || '').replace(/\D/g, '').slice(0, 4);
        },
        logout() {
            this.$store.dispatch('logout').then(() => {
                sessionStorage.removeItem('valzum_app_lock_unlocked_at');
                sessionStorage.removeItem('valzum_idle_last_activity_at');
                sessionStorage.removeItem('valzum_staff_idle_locked');
                sessionStorage.removeItem('valzum_native_password_due');
                this.locked = false;
                this.idleLocked = false;
                this.idleReason = '';
                this.$router.push({name: 'Login'});
            });
        }
    }
}
</script>

<style scoped>
.app-lock-card {
    border-radius: 8px;
}

.app-lock-card--fullscreen {
    border-radius: 0 !important;
    min-height: 100%;
}

.app-lock-card--staff {
    background: transparent;
}

.app-lock-title {
    flex: 0 0 auto;
    justify-content: center;
    line-height: 1.25;
    min-height: 52px;
    word-break: normal;
}

.app-lock-body {
    min-height: 0;
    overflow-y: auto;
}

.app-lock-account {
    border-radius: 8px;
    padding: 12px;
}

.app-lock-dynamic-key {
    background: rgba(255, 255, 255, .72);
    border: 1px solid rgba(13, 147, 165, .18);
    cursor: pointer;
    font-variant-numeric: tabular-nums;
    max-width: 280px;
}

.app-lock-dynamic-key--inline {
    flex: 0 0 auto;
    max-width: 280px;
    width: calc(100% - 32px);
}

.theme--dark .app-lock-dynamic-key {
    background: rgba(18, 52, 59, .72);
}

.min-width-0 {
    min-width: 0;
}

.staff-lock-screen {
    align-items: stretch;
    background: linear-gradient(135deg, var(--v-primary-base) 0%, #073642 100%);
    color: #ffffff;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    overflow-y: auto;
    padding: calc(18px + env(safe-area-inset-top)) 24px calc(18px + env(safe-area-inset-bottom));
}

.staff-lock-top,
.staff-lock-content,
.staff-lock-footer {
    margin-left: auto;
    margin-right: auto;
    max-width: 1120px;
    width: 100%;
}

.staff-lock-top {
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    justify-content: space-between;
    min-height: 52px;
}

.staff-lock-logo {
    flex: 0 0 auto;
    max-height: 42px;
    max-width: 180px;
}

.staff-lock-chip {
    background: rgba(255, 255, 255, .14) !important;
    color: #ffffff !important;
}

.staff-lock-content {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    justify-content: center;
    padding: 28px 0;
}

.staff-lock-panel {
    background: rgba(255, 255, 255, .96);
    border: 1px solid rgba(255, 255, 255, .26);
    border-radius: 8px;
    color: rgba(0, 0, 0, .87);
    max-width: 460px;
    padding: 30px;
    width: 100%;
}

.theme--dark .staff-lock-panel {
    background: rgba(15, 33, 39, .96);
    color: #ffffff;
}

.staff-lock-account {
    align-items: center;
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
}

.staff-lock-avatar {
    flex: 0 0 auto;
}

.staff-lock-name {
    color: var(--v-primary-base);
    font-size: 1.12rem;
    font-weight: 700;
    line-height: 1.25;
}

.theme--dark .staff-lock-name {
    color: var(--v-secondary-base);
}

.staff-lock-copy {
    margin-bottom: 20px;
}

.staff-lock-heading {
    color: var(--v-primary-base);
    font-size: 1.55rem;
    font-weight: 800;
    line-height: 1.15;
    margin-bottom: 6px;
}

.theme--dark .staff-lock-heading {
    color: var(--v-secondary-base);
}

.staff-lock-message {
    color: rgba(0, 0, 0, .62);
    font-size: .95rem;
    line-height: 1.45;
}

.theme--dark .staff-lock-message {
    color: rgba(255, 255, 255, .72);
}

.staff-lock-footer {
    color: rgba(255, 255, 255, .78);
    flex: 0 0 auto;
    font-size: .82rem;
    font-weight: 600;
    text-align: center;
}

@media (max-width: 600px) {
    .app-lock-card {
        border-radius: 0 !important;
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 100%;
    }

    .app-lock-body {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        justify-content: center;
        padding-bottom: calc(16px + env(safe-area-inset-bottom)) !important;
        padding-top: 16px !important;
    }

    .app-lock-actions {
        flex: 0 0 auto;
        padding-bottom: calc(12px + env(safe-area-inset-bottom));
    }

    .staff-lock-screen {
        padding: calc(14px + env(safe-area-inset-top)) 16px calc(14px + env(safe-area-inset-bottom));
    }

    .staff-lock-top {
        min-height: 44px;
    }

    .staff-lock-logo {
        max-height: 34px;
        max-width: 144px;
    }

    .staff-lock-chip {
        font-size: .68rem;
        height: 24px;
    }

    .staff-lock-content {
        padding: 16px 0;
    }

    .staff-lock-panel {
        padding: 22px 18px;
    }

    .staff-lock-account {
        gap: 12px;
        margin-bottom: 18px;
    }

    .staff-lock-heading {
        font-size: 1.32rem;
    }

    .staff-lock-footer {
        font-size: .75rem;
    }
}
</style>
